
import CarouselProduct from './CarouselProduct.vue';
import CarouselTrackingService from 'JS/services/CarouselTrackingService';
import Reviews from '../ReviewStars.vue';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { createCarouselClickData, createCarouselClickGtmData, createCarouselViewData, createCarouselViewGtmData, handleTrackingLinkClick } from '../../utilities/carousel-tracking';
import { createListSlider, IListSlider } from 'JS/list-slider';
import { IHawkRecommendationsCarouselData, IProductCarousel, IProductCarouselProduct, isHawkCarousel, ProductCarousel } from '../../types/Carousel';
import { isTabletWidth, onBreakpointChange } from 'JS/utilities/viewport';
import { lazyLoadImages } from '../../global/images';
import { ViewportListener } from 'JS/types/Media';

const _trackingSvc = new CarouselTrackingService();

@Component({
	name: 'ProductsCarousel',
	components: {
		CarouselProduct,
		Reviews
	}
})
export default class ProductsCarousel extends Vue {

	$el!: HTMLElement;
	$refs!: {
		prevBtn: HTMLButtonElement,
		nextBtn: HTMLButtonElement
	}

	/* Props
	============================================*/

	@Prop({ type: Object as () => ProductCarousel, required: false })
	readonly carousel: ProductCarousel;

	@Prop({ type: String, required: false, default: '' })
	readonly customTrackingUrl: string;

	@Prop({ type: Object, required: false })
	readonly customClickData: IHawkRecommendationsCarouselData;

	@Prop({ type: Boolean })
	readonly loading: boolean;

	/* Computed
	============================================*/

	get products(): IProductCarouselProduct[] {
		if (this.loading) {
			return [];
		}

		return this.carousel?.products ?? [];
	}

	/* Data
	============================================*/

	labelId: string = Math.random().toString(36).substr(2, 10);
	viewImpressionUrl: string | null = null;
	slider: IListSlider | null;
	listeners: ViewportListener[] = [];

	/* Methods
	============================================*/

	init() {
		if (this.loading) return;
		this.$nextTick(() => {
			lazyLoadImages(this.$el);
			if (!this.slider && !isTabletWidth()) {
				this.slider = createListSlider(this.$el);
				this.slider.setControls(this.$refs.prevBtn, this.$refs.nextBtn);
				this.listeners.push(onBreakpointChange(() => this.slider.reset()));
			}
		});
		this.trackView();
	}

	trackClick(e: MouseEvent, product: IProductCarouselProduct) {
		const customClickData = isHawkCarousel(this.carousel)
			? {
				hawkRequestId: this.carousel.requestId,
				hawkWidgetType: this.carousel.widgetType,
			}
			: null;

		const clickData = createCarouselClickData(
			product,
			this.carousel,
			customClickData,
			this.viewImpressionUrl
		);

		const gtmData = createCarouselClickGtmData(product, this.carousel);

		handleTrackingLinkClick(e, () => _trackingSvc.trackClick(clickData, gtmData));
	}

	async trackView() {
		if (this.viewImpressionUrl || this.loading || !this.carousel) return;

		this.viewImpressionUrl = this.customTrackingUrl
			// keep track of this URL; if the browser URL changes (like when selecting child skus on the PDP), we
			// want to use the same URL for click events later so that they match up with this view event
			|| (window.location.pathname + window.location.search);

		const viewData = createCarouselViewData(this.carousel, this.viewImpressionUrl);
		const gtmData = createCarouselViewGtmData(this.carousel);

		await _trackingSvc.trackViews([viewData],[gtmData]);
	}

	/* Lifecycle Hooks
	============================================*/

	created() {
		this.slider = null;
	}

	mounted() {
		this.init();
		this.listeners.push(onBreakpointChange(() => this.init()));
	}

	beforeDestroy() {
		this.listeners.forEach(x => x.remove());
	}

	/* Watchers
	============================================*/

	@Watch('carousel')
	onCarouselChanged(newCarousel: IProductCarousel, prevCarousel: IProductCarousel) {
		if (!newCarousel) return;
		if (prevCarousel) {
			// track again if this is a new carousel
			this.viewImpressionUrl = null;
		}
		this.init();
	}

	@Watch('loading')
	onLoadingChanged(isLoading: boolean) {
		if (isLoading) return;
		this.init();
	}

}

